// Protected surface: review docs/protected-surfaces/session-pwa-transport.md before changing auth request, CSRF, refresh, or retry behavior.
/**
 * authApiClient.js
 * 
 * This file creates and exports a custom Axios instance that handles authentication
 * and token management for API requests in our application.
 * 
 * Key features:
 * 1. Centralized API configuration: Sets up a base URL for all API requests.
 * 2. Automatic token injection: Adds the authentication token to every outgoing request.
 * 3. Token refresh mechanism: Automatically refreshes the access token if it expires.
 * 4. Error handling: Manages authentication errors and redirects to login when needed.
 * 
 * How it works:
 * - The Axios instance is created with a base URL from the environment variables.
 * - A request interceptor adds the authentication token to each request's headers.
 * - A response interceptor catches 401 (Unauthorized) errors.
 * - If a 401 error occurs, it attempts to refresh the token using the refresh token.
 * - If the token refresh is successful, it retries the original request.
 * - If the refresh fails, it clears the authentication cookies and redirects to the login page.
 * 
 * Usage:
 * Import this client in your components or services and use it for making API calls.
 * It will handle authentication automatically, allowing you to focus on the API logic.
 * 
 * Example:
 * import authApiClient from './authApiClient';
 * const response = await authApiClient.get('/some-endpoint');
 * 
 * Note: Ensure that your backend supports the token refresh mechanism as implemented here.
 */

// src/authApiClient.js

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { safeGetItem, safeRemoveItem } from './utils/storageSafe';
import { getRefreshPromise, isSessionReady, waitForSessionReady } from './utils/authManager';
import { getMissionData } from './utils/missionDataHelper';
import { readLastTurnIdForSession } from './utils/journeyTurnLinkStore';
import {
  getOrCreateTestRunId,
  isJourneyE2ETestModeEnabled,
  shouldAttachTestRunToUrl,
  appendTestRunLog
} from './utils/testRunContext';
import {
  validateContinuityEnvelope,
  applyContinuityEnvelope
} from './utils/continuityContract';
import { API_BASE_URL } from './utils/apiBase';

// --- CHANGE 1: Memory Storage for Token ---
// Partitioned cookies are sometimes invisible to document.cookie in iframes/embedded contexts.
// We store the valid token in memory once the server sends it to us.
let inMemoryCSRFToken = null;

// --- CHANGE 2: Robust Cookie Reader ---
export function getCookie(name) {
  // 1. If we have a verified token from the server in memory, use it.
  if (name === 'FS_API_XSRF' && inMemoryCSRFToken) {
    return inMemoryCSRFToken;
  }

  // 2. Fallback to parsing document.cookie
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key === name && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Returns the most up-to-date CSRF token, preferring the in-memory one
 * synchronized from server response headers.
 */
export function getCurrentCSRFToken() {
  return inMemoryCSRFToken || getCookie('FS_API_XSRF');
}

// Create axios instance with default configuration
const authApiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
});

// Configure Axios for automatic CSRF handling with unique names
authApiClient.defaults.xsrfCookieName = 'FS_API_XSRF';
authApiClient.defaults.xsrfHeaderName = 'X-FS-XSRF';

// CSRF and API configuration (no logging)

// Abort any outgoing request immediately after logout
authApiClient.interceptors.request.use(
  (config) => {
    const url = (config?.url || '').toString();
    const isLogout = /\/logout(?:$|\?|#|$)/.test(url);
    if (typeof window !== 'undefined' && window.__LOGGED_OUT && !isLogout) {
      return Promise.reject(new axios.Cancel('Aborted due to logout'));
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request interceptor to add auth headers
authApiClient.interceptors.request.use(
  (config) => {
    // ✅ DIAGNOSTIC: Log ALL requests in production when debug is enabled
    if (process.env.NODE_ENV === 'production' && window.debugStatus && window.debugStatus().enabled) {
      console.log('[authApiClient] 🔍 REQUEST:', {
        method: config.method,
        url: config.url,
        fullURL: `${authApiClient.defaults.baseURL}${config.url}`,
        baseURL: authApiClient.defaults.baseURL,
        hasCSRFHeader: !!config.headers['X-FS-XSRF'],
        cookies: document.cookie
      });
    }

    // Log rememberMe setting and cookie status
    const rememberMeSetting = safeGetItem('fusesoul_remember_me') === 'true';

    // ✅ IMPROVED: Check both localStorage and server-side cookie for rememberMe
    const serverRememberMe = document.cookie.match(/(?:^| )FS_REMEMBER=([^;]+)/);
    const finalRememberMe = rememberMeSetting || (serverRememberMe && serverRememberMe[1] === '1');

    // ✅ CRITICAL: Always add CSRF token header for POST/PUT/PATCH/DELETE requests
    const headerName = 'X-FS-XSRF';
    const headerNameLower = 'x-fs-xsrf';
    const isMutatingMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase());

    // --- CHANGE 4: Use In-Memory Token with Cookie Fallback ---
    if (isMutatingMethod && !config.headers[headerName] && !config.headers[headerNameLower]) {
      // Priority: 1. In-Memory (from previous response) 2. Cookie
      const xsrfToken = inMemoryCSRFToken || getCookie('FS_API_XSRF');

      if (xsrfToken) {
        config.headers[headerName] = xsrfToken;
        httpClientDebug('[authApiClient] CSRF header added for', config.method, config.url, 'Token:', xsrfToken.substring(0, 10) + '...', 'Source:', inMemoryCSRFToken ? 'memory' : 'cookie');
      } else {
        console.warn('[authApiClient] ⚠️ No FS_API_XSRF token found for', config.method, config.url);
      }
    } else if (isMutatingMethod) {
      const existingToken = config.headers[headerName] || config.headers[headerNameLower];
      httpClientDebug('[authApiClient] CSRF header already present for', config.method, config.url, ':', existingToken?.substring(0, 10) + '...');
    }

    // Debug logging (only for mutating methods to reduce noise)
    if (isMutatingMethod) {
      httpClientDebug('[authApiClient] Request interceptor:', {
        url: config.url,
        method: config.method,
        csrfHeader: config.headers[headerName] || config.headers[headerNameLower] ? 'Present' : 'Missing',
        hasCookie: !!document.cookie.match(/FS_API_XSRF=/)
      });
    }

    return config;
  },
  (error) => {
    console.error('[authApiClient] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ✅ UPDATED: Use global auth manager for single-flight refresh
// Removed local refreshInFlight - now handled by authManager
let lastRefreshFailedAt = 0;
const REFRESH_FAIL_COOLDOWN_MS = 5000;
const RATE_LIMIT_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes - don't retry refresh if rate limited

// ✅ FIX: Allow refresh for session probes even on public pages
function isSessionProbe(url) {
  const u = (url || '').toString();
  return /\/(users\/me|session)(?:$|\?|#)/.test(u);
}

function hasRememberMeCookie() {
  return /(?:^|;\s*)FS_REMEMBER=1(?:;|$)/.test(document.cookie);
}

function isPublicLocation() {
  try {
    if (typeof window === 'undefined') return false;
    const p = window.location.pathname || '';
    return [
      '/',
      '/login',
      '/signup',
      '/pricing',
      '/payment-success',
      '/forgot-password',
      '/reset-password',
      '/about',
      '/contact',
      '/unauthorized',
      '/reactivate',
      '/test-reactivate',
      '/inspiration'
    ].some(prefix => p === prefix || p.startsWith(prefix));
  } catch {
    return false;
  }
}

function isAuthEndpoint(url) {
  try {
    const u = (url || '').toString();
    return (
      u.includes('/refresh_tokens') ||
      u.includes('/auth/refresh') ||
      u.includes('/logout') ||
      u.includes('/health')
    );
  } catch {
    return false;
  }
}

// --- CHANGE 3: Response Interceptor to Sync Token ---
// Response interceptor to handle token refresh
const HTTP_CLIENT_DEBUG = String(process.env.REACT_APP_HTTP_CLIENT_DEBUG || '').trim() === '1';
const httpClientDebug = (...args) => {
  if (HTTP_CLIENT_DEBUG) console.log(...args);
};

authApiClient.interceptors.response.use(
  (response) => {
    // If the server sent us a fresh CSRF token in the header, grab it.
    // This bypasses cookie shadowing issues.
    const csrfHeader = response.headers['x-fs-xsrf'] || response.headers['x-xsrf-token'];
    if (csrfHeader) {
      inMemoryCSRFToken = csrfHeader;
      httpClientDebug('[authApiClient] CSRF token synced from response header:', csrfHeader.substring(0, 10) + '...');
    }

    httpClientDebug('[authApiClient] Response received:', {
      url: response.config.url,
      status: response.status,
      statusText: response.statusText
    });

    // ✅ OPTIONAL IMPROVEMENT: Background refresh when server hints it's needed
    if (response.headers['x-refresh-needed'] === '1') {
      httpClientDebug('[authApiClient] Server hints refresh needed, queuing background refresh');

      // ✅ FIX: Use global single-flight refresh promise
      getRefreshPromise(authApiClient).catch(() => { }); // non-blocking
    }

    return response;
  },
  async (error) => {
    const requestForCsrf = error.config || {};

    // If we get a 403 CSRF error, clear our memory token so we retry fresh
    if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID') {
      console.warn('[authApiClient] ⚠️ CSRF token invalid, clearing in-memory token');
      inMemoryCSRFToken = null;

      // Attempt auto-heal from error headers if available (backend sends it)
      const newCsrfToken = error.response.headers['x-fs-xsrf'] || error.response.headers['x-xsrf-token'];
      if (newCsrfToken) {
        httpClientDebug('[authApiClient] Auto-healing CSRF token from error header');
        inMemoryCSRFToken = newCsrfToken;
      }

      // Retry the request once
      if (!requestForCsrf._retry) {
        httpClientDebug('[authApiClient] Retrying request with fresh CSRF token');
        requestForCsrf._retry = true;

        // Ensure the next request grabs the fresh token by removing the old one 
        // to trigger the request interceptor logic again
        if (requestForCsrf.headers) {
          delete requestForCsrf.headers['X-FS-XSRF'];
          delete requestForCsrf.headers['x-fs-xsrf'];
        }
        return authApiClient(requestForCsrf);
      }
    }

    const originalRequest = error.config || {};

    // ✅ CRITICAL FIX: Early exit if logged out
    if (typeof window !== 'undefined' && window.__LOGGED_OUT) {
      return Promise.reject(error);
    }

    // 🚫 Never refresh on public pages without Remember-Me, or on /login
    const pathname = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
    const isAuthEndpointRequest = isAuthEndpoint(originalRequest.url);
    const noRemember = !hasRememberMeCookie();
    const onPublic = isPublicLocation();
    const isLoginPage = pathname === '/login';

    if (isAuthEndpointRequest || (onPublic && noRemember) || isLoginPage) {
      httpClientDebug('[authApiClient] Skip refresh (auth endpoint or public w/o remember or /login).', {
        isAuthEndpointRequest, onPublic, noRemember, isLoginPage
      });
      return Promise.reject(error);
    }

    // Log error details
    const errorDetails = {
      url: originalRequest?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      code: error.response?.data?.code,
      message: error.message,
      fullURL: `${authApiClient.defaults.baseURL}${originalRequest?.url}`,
      baseURL: authApiClient.defaults.baseURL,
      responseData: error.response?.data
    };

    // ✅ DIAGNOSTIC: Always log errors in production when debug is enabled
    if (process.env.NODE_ENV === 'production' && window.debugStatus && window.debugStatus().enabled) {
      console.error('[authApiClient] ❌ ERROR:', errorDetails);
    } else {
      httpClientDebug('[authApiClient] Response interceptor error:', errorDetails);
    }

    // ✅ CRITICAL FIX: Handle 429 rate limit errors - don't retry refresh
    if (error.response?.status === 429) {
      const errorCode = error.response?.data?.code;
      if (errorCode === 'REFRESH_RATE_LIMIT_EXCEEDED' || originalRequest.url?.includes('/refresh_tokens')) {
        if (typeof window !== 'undefined') {
          window.__RATE_LIMITED = true;
          window.__RATE_LIMIT_UNTIL = Date.now() + RATE_LIMIT_COOLDOWN_MS;

          console.warn('[authApiClient] Rate limited on refresh token endpoint. Stopping refresh attempts for', RATE_LIMIT_COOLDOWN_MS / 1000, 'seconds');

          // Clear stored flags and redirect to login since we can't refresh
          safeRemoveItem('fusesoul_remember_me');

          // Only redirect if not already on login page
          if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
            httpClientDebug('[authApiClient] Redirecting to login due to rate limit');
            window.location.href = '/login';
          }
        }
      }
      return Promise.reject(error);
    }

    // ✅ FIX: Don't attempt refresh if we're rate limited
    if (typeof window !== 'undefined' && window.__RATE_LIMITED) {
      const rateLimitUntil = window.__RATE_LIMIT_UNTIL || 0;
      if (Date.now() < rateLimitUntil) {
        httpClientDebug('[authApiClient] Skipping refresh attempt - still rate limited');
        return Promise.reject(error);
      } else {
        // Rate limit expired, clear flag
        window.__RATE_LIMITED = false;
        window.__RATE_LIMIT_UNTIL = 0;
      }
    }

    // If the error is 401 (Unauthorized) and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark request as retried
      originalRequest._retry = true;

      // ✅ CRITICAL FIX: Don't retry if we just failed a refresh (cooldown)
      const timeSinceLastRefreshFail = Date.now() - lastRefreshFailedAt;
      if (timeSinceLastRefreshFail < REFRESH_FAIL_COOLDOWN_MS) {
        httpClientDebug('[authApiClient] Skipping refresh - recent refresh failure, cooldown active');
        return Promise.reject(error);
      }

      // ✅ FIX: Wait for session to be ready before attempting refresh
      // This prevents refresh attempts before bootstrap completes
      if (!isSessionReady()) {
        httpClientDebug('[authApiClient] Session not ready, waiting for bootstrap...');
        try {
          await waitForSessionReady();
          // Session is ready now, retry original request
          httpClientDebug('[authApiClient] Session ready, retrying original request');
          return authApiClient(originalRequest);
        } catch (waitError) {
          console.error('[authApiClient] Failed waiting for session:', waitError);
          return Promise.reject(error);
        }
      }

      try {
        httpClientDebug('[authApiClient] Attempting token refresh...');

        // ✅ FIX: Use global single-flight refresh promise (prevents parallel refresh attempts across entire app)
        const refreshResult = await getRefreshPromise(authApiClient);

        if (!refreshResult.success) {
          throw new Error(refreshResult.error || 'Refresh failed');
        }

        httpClientDebug('[authApiClient] Token refresh successful, retrying original request');
        return authApiClient(originalRequest);
      } catch (refreshError) {
        lastRefreshFailedAt = Date.now();
        console.error('[authApiClient] Token refresh failed:', {
          status: refreshError.response?.status,
          code: refreshError.response?.data?.code,
          message: refreshError.response?.data?.message || refreshError.message
        });

        // ✅ FIX: Handle rate limit on refresh attempt (already handled in authManager, but double-check)
        if (refreshError.response?.status === 429) {
          if (typeof window !== 'undefined') {
            window.__RATE_LIMITED = true;
            window.__RATE_LIMIT_UNTIL = Date.now() + RATE_LIMIT_COOLDOWN_MS;
          }
        }

        // Clear stored flags
        safeRemoveItem('fusesoul_remember_me');

        // Redirect to login page (final fallback) - but only if not already there
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/signup') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshError);
      }
    }

    // For other errors, just reject
    return Promise.reject(error);
  }
);

// ========================================
// 🎯 TRACE INTERCEPTORS - Add trace IDs to follow requests
// ========================================

// Helper to skip trace header for certain requests
const skipTrace = (url = '') =>
  url.includes('/health') ||
  url.includes('/logger-config');

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function resolveContinuityEnvelope(config = {}) {
  const payload = isPlainObject(config.data) ? config.data : null;
  const continuitySeed = isPlainObject(payload?.uiState?.continuitySeed)
    ? payload.uiState.continuitySeed
    : null;
  const missionData = getMissionData(false) || {};
  const missionSessionIdRaw = String(
    payload?.missionSessionId
    || payload?.activeMissionId
    || continuitySeed?.missionSessionId
    || missionData?.missionSessionId
    || missionData?.activeMissionId
    || missionData?.missionId
    || ''
  ).trim() || null;
  const lastTurnIdCandidate = String(
    payload?.lastTurnId
    || continuitySeed?.lastTurnId
    || (missionSessionIdRaw ? readLastTurnIdForSession(missionSessionIdRaw) : '')
    || ''
  ).trim() || null;
  const lastTurnIdRaw = missionSessionIdRaw ? lastTurnIdCandidate : null;
  const continuityValidation = validateContinuityEnvelope({
    missionSessionId: missionSessionIdRaw,
    activeMissionId: missionSessionIdRaw,
    lastTurnId: lastTurnIdRaw
  }, { allowMissingSession: true });

  return {
    payload,
    continuityValidation,
    missionSessionId: continuityValidation.envelope.missionSessionId,
    lastTurnId: continuityValidation.envelope.lastTurnId
  };
}

// Request interceptor - add trace ID to every request (except health)
authApiClient.interceptors.request.use((config) => {
  const traceId = uuidv4();
  config.headers = config.headers || {};
  const normalizedUrl = String(config?.url || '');
  const shouldAttachJourneyContext = shouldAttachTestRunToUrl(normalizedUrl);

  if (shouldAttachJourneyContext) {
    const { payload, missionSessionId, lastTurnId, continuityValidation } = resolveContinuityEnvelope(config);
    if (payload) {
      applyContinuityEnvelope(payload, {
        missionSessionId,
        activeMissionId: missionSessionId,
        lastTurnId
      });
    } else {
      const normalizedParams = applyContinuityEnvelope({
        missionSessionId: config.params?.missionSessionId || missionSessionId,
        activeMissionId: config.params?.activeMissionId || missionSessionId,
        lastTurnId: config.params?.lastTurnId || lastTurnId
      }, {
        missionSessionId,
        activeMissionId: missionSessionId,
        lastTurnId
      });
      config.params = {
        ...(config.params || {}),
        ...(normalizedParams?.missionSessionId
          ? {
            missionSessionId: normalizedParams.missionSessionId,
            activeMissionId: normalizedParams.activeMissionId
          }
          : {}),
        ...(normalizedParams?.lastTurnId
          ? {
            lastTurnId: normalizedParams.lastTurnId
          }
          : {})
      };
    }

    let resolvedTestRunId = '';
    if (isJourneyE2ETestModeEnabled()) {
      const testRunId = getOrCreateTestRunId();
      if (testRunId) {
        resolvedTestRunId = testRunId;
        config.headers['x-test-run-id'] = testRunId;
        if (payload && !payload.testRunId) {
          payload.testRunId = testRunId;
        } else if (!payload) {
          config.params = {
            ...(config.params || {}),
            testRunId: config.params?.testRunId || testRunId
          };
        }
      }
    }

    if (continuityValidation.warnings.length > 0 || continuityValidation.errors.length > 0) {
      appendTestRunLog({
        event: 'continuity_validation',
        url: normalizedUrl,
        method: String(config.method || 'get').toUpperCase(),
        warnings: continuityValidation.warnings,
        errors: continuityValidation.errors
      }, resolvedTestRunId);
    }

    appendTestRunLog({
      event: 'http_request',
      url: normalizedUrl,
      method: String(config.method || 'get').toUpperCase(),
      missionSessionId: missionSessionId || null,
      lastTurnId: lastTurnId || null
    }, resolvedTestRunId);
  }

  // Skip trace header for health/CSRF calls to avoid CORS issues
  if (!skipTrace(config.url)) {
    config.headers['x-trace-id'] = traceId;
  }

  httpClientDebug(`[trace ${traceId}] FE -> ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Response interceptor - log completion with trace ID
authApiClient.interceptors.response.use(
  (res) => {
    const traceId = res.config.headers['x-trace-id'];
    httpClientDebug(`[trace ${traceId}] FE <- ${res.status} ${res.config.url} size=${JSON.stringify(res.data).length}`);
    if (shouldAttachTestRunToUrl(String(res.config?.url || ''))) {
      appendTestRunLog({
        event: 'http_response',
        url: res.config?.url || '',
        status: res.status,
        traceId
      }, res.headers?.['x-test-run-id'] || '');
    }
    return res;
  },
  (err) => {
    const traceId = err.config?.headers?.['x-trace-id'];
    httpClientDebug(`[trace ${traceId}] FE <- ERROR ${err.response?.status || 'NETWORK'} ${err.config?.url || 'unknown'}`);
    if (shouldAttachTestRunToUrl(String(err.config?.url || ''))) {
      appendTestRunLog({
        event: 'http_error',
        url: err.config?.url || '',
        status: err.response?.status || null,
        traceId,
        message: err.message || 'request_failed'
      }, err.config?.headers?.['x-test-run-id'] || '');
    }
    throw err;
  }
);

// Helper function to check if user is authenticated
export const isAuthenticated = async () => {
  try {
    const { status } = await authApiClient.get('/users/me');
    return status === 200;
  } catch {
    return false;
  }
};

// Helper function to get auth headers
export const getAuthHeaders = () => {
  // Tokens are now in HttpOnly cookies - no need for Authorization header
  return {};
};

// Helper function to handle logout
export const handleLogout = () => {
  httpClientDebug('[authApiClient] Handling logout');
  safeRemoveItem('fusesoul_remember_me');
  if (typeof window !== 'undefined') {
    sessionStorage.clear();
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
  httpClientDebug('[authApiClient] Logout completed, storage cleared');
};

// Helper function to refresh token manually
export const manualTokenRefresh = async () => {
  try {
    httpClientDebug('[authApiClient] Manual token refresh requested');
    const rememberMeSetting = safeGetItem('fusesoul_remember_me') === 'true';
    httpClientDebug('[authApiClient] RememberMe setting during token refresh:', {
      rememberMe: rememberMeSetting
    });
    const response = await axios.post(
      `${authApiClient.defaults.baseURL}/refresh_tokens`,
      {},
      { withCredentials: true }
    );
    httpClientDebug('[authApiClient] Manual token refresh successful:', {
      status: response.status,
      rememberMe: response.data.rememberMe
    });
    return response.data;
  } catch (error) {
    console.error('[authApiClient] Manual token refresh failed:', error);
    throw error;
  }
};

export default authApiClient;
